import {
  ActivatedRouteSnapshot,
  Route,
  Routes,
  UrlMatchResult,
  UrlSegment,
  UrlSegmentGroup,
} from '@angular/router';

import { environment } from 'src/environments/environment';

import { authGuard } from './auth.guard';
import { superAdminGuard } from './portal/lead-crm/super-admin.guard';
import { brandAccessGuard } from './portal/pages/brand-page/brand-access.guard';
import { periodEndedGuard } from './portal/period-ended/period-ended.guard';
import { PORTAL_DROPDOWN_TEXTS } from './portal/portal-dropdown-texts';
import { comparisonVisitResolver } from './portal/preferences/comparison-visit.resolver';
import { memberPreferencesResolver } from './portal/preferences/member-preferences.resolver';
import { DROPDOWN_TEXTS } from './shared/components/dropdown/dropdown-texts';
import { COUNTRY_CONFIG } from './shared/country/country.config';
import { countryGuard } from './shared/country/country.guard';
import { featureGuard } from './shared/country/feature.guard';
import { legalArticleGuard } from './shared/country/legal-article.guard';

// Market-localized campaigns URL, resolved at build time from the (file-replaced)
// environment so a foreign market never serves the Swedish path. Declared once in
// country.config.ts and reused by the nav links and the canonical tag.
const CAMPAIGNS_PATH = COUNTRY_CONFIG[environment.country].campaignsPath;
const LEGACY_CAMPAIGNS_PATH = 'article/alla-rabatter-och-kampanjer';

function categoryMatcher(
  segments: UrlSegment[],
  group: UrlSegmentGroup,
  route: Route
): UrlMatchResult {
  // match urls like "/category/:category" where category can contain '/'
  if (segments.length > 0) {
    // if first segment is 'category', then concat all the next segments into a single one
    // and return it as a parameter named 'category'
    if (segments[0].path === 'category') {
      return {
        consumed: segments,
        posParams: {
          category: new UrlSegment(segments.slice(1).join('/'), {}),
        },
      };
    }
  }
  return null!;
}

export const routes: Routes = [
  // Reactivate page (standalone, no sidebar)
  {
    path: 'portal/reactivate',
    canActivate: [countryGuard('SE')],
    loadComponent: () =>
      import('./portal/reactivate/reactivate.component').then((m) => m.ReactivateComponent),
  },
  {
    path: 'portal/period-ended',
    canActivate: [periodEndedGuard],
    loadComponent: () =>
      import('./portal/period-ended/period-ended.component').then((m) => m.PeriodEndedComponent),
  },
  // Portal landing page (no auth required)
  // The brand portal and its B2B pitch page are a Swedish-market product: the
  // data, pricing and sales motion are all SE. countryGuard keeps them off the
  // EU hosts entirely rather than merely out of the EU sitemaps.
  {
    path: 'portal-landing',
    canActivate: [countryGuard('SE')],
    loadComponent: () =>
      import('./portal/portal-landing/portal-landing.component').then(
        (m) => m.PortalLandingComponent
      ),
  },
  {
    path: 'b2b',
    canActivate: [countryGuard('SE')],
    loadComponent: () =>
      import('./portal/portal-landing/portal-landing.component').then(
        (m) => m.PortalLandingComponent
      ),
  },
  // Portal routes with portal layout (auth required)
  // countryGuard runs before authGuard so an EU visitor lands on the market
  // home page instead of a Swedish login screen.
  {
    path: 'portal',
    canActivate: [countryGuard('SE'), authGuard],
    resolve: { _prefs: memberPreferencesResolver },
    providers: [{ provide: DROPDOWN_TEXTS, useValue: PORTAL_DROPDOWN_TEXTS }],
    loadComponent: () =>
      import('./portal/portal-layout/portal-layout.component').then((m) => m.PortalLayoutComponent),
    children: [
      {
        path: 'map',
        loadComponent: () =>
          import('./portal/portal-map-view/portal-map-view.component').then(
            (m) => m.PortalMapViewComponent
          ),
      },
      {
        path: 'reviews',
        loadComponent: () =>
          import('./portal/pages/reviews-page/reviews-page.component').then(
            (m) => m.ReviewsPageComponent
          ),
      },
      {
        path: 'scorecard',
        loadComponent: () =>
          import('./portal/scorecard/scorecard.component').then((m) => m.ScorecardComponent),
      },
      {
        path: 'distribution',
        loadComponent: () =>
          import('./portal/distribution/distribution.component').then(
            (m) => m.DistributionComponent
          ),
      },
      {
        path: 'brand-positioning',
        loadComponent: () =>
          import('./portal/brand-positioning/brand-positioning.component').then(
            (m) => m.BrandPositioningComponent
          ),
      },
      {
        path: 'campaign-dashboard',
        loadComponent: () =>
          import('./portal/campaign-dashboard/campaign-dashboard.component').then(
            (m) => m.CampaignDashboardComponent
          ),
      },
      {
        path: 'api',
        loadComponent: () =>
          import('./portal/api-docs/api-docs.component').then((m) => m.ApiDocsComponent),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./portal/settings/settings.component').then((m) => m.SettingsComponent),
      },
      {
        path: 'aposell',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./portal/lead-crm/lead-crm.component').then((m) => m.LeadCrmComponent),
      },
      {
        path: 'subscriptions',
        loadComponent: () =>
          import('./portal/subscriptions/subscriptions.component').then(
            (m) => m.SubscriptionsComponent
          ),
      },
      {
        path: 'alerts',
        loadComponent: () =>
          import('./portal/alerts/alerts.component').then((m) => m.AlertsComponent),
      },
      {
        path: 'coverage',
        loadComponent: () =>
          import('./portal/coverage/coverage.component').then((m) => m.CoverageComponent),
      },
      {
        path: 'coverage/review',
        loadComponent: () =>
          import('./portal/coverage/coverage-review/coverage-review.component').then(
            (m) => m.CoverageReviewComponent
          ),
      },
      {
        path: 'knowledge-base',
        loadComponent: () =>
          import('./portal/knowledge-base/knowledge-base.component').then(
            (m) => m.KnowledgeBaseComponent
          ),
      },
      {
        path: 'help',
        loadComponent: () => import('./portal/help/help.component').then((m) => m.HelpComponent),
      },
      {
        path: 'network',
        loadComponent: () =>
          import('./portal/network/network-graph.component').then((m) => m.NetworkGraphComponent),
      },
      {
        path: 'toplists',
        loadComponent: () =>
          import('./portal/toplists/toplists.component').then((m) => m.ToplistsComponent),
      },
      {
        path: 'compare/:pbKey',
        resolve: { _visit: comparisonVisitResolver },
        loadComponent: () =>
          import('./portal/pages/comparison-page/comparison-page.component').then(
            (m) => m.ComparisonPageComponent
          ),
      },
      {
        path: ':brandId',
        canActivate: [brandAccessGuard],
        loadComponent: () =>
          import('./portal/pages/brand-page/brand-page.component').then(
            (m) => m.BrandPageComponent
          ),
      },
      {
        path: '',
        loadComponent: () =>
          import('./portal/pages/main-portal-page/main-portal-page.component').then(
            (m) => m.MainPortalPageComponent
          ),
      },
    ],
  },
  // Login and Register routes (standalone, no layout)
  // Both exist only to reach the brand portal, which is SE-only. Without the
  // guard an EU visitor could authenticate here and then be bounced home by the
  // guard on /portal, leaving a login that works but leads nowhere.
  {
    path: 'login',
    canActivate: [countryGuard('SE')],
    loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'register',
    canActivate: [countryGuard('SE')],
    loadComponent: () => import('./register/register.component').then((m) => m.RegisterComponent),
  },
  // Consumer app routes with consumer layout
  {
    path: '',
    loadComponent: () =>
      import('./layouts/consumer-layout/consumer-layout.component').then(
        (m) => m.ConsumerLayoutComponent
      ),
    children: [
      {
        path: 'lagerstatus',
        loadComponent: () =>
          import('./dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'lager',
        loadComponent: () =>
          import('./dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'finns-i-lager',
        loadComponent: () =>
          import('./dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'search/:search',
        loadComponent: () =>
          import('./products/product-list/product-list.component').then(
            (m) => m.ProductListComponent
          ),
        // Meta tags for search are set by ProductListComponent via SeoService
      },
      {
        path: 'tag/:tag',
        loadComponent: () =>
          import('./products/product-list/product-list.component').then(
            (m) => m.ProductListComponent
          ),
      },
      {
        matcher: categoryMatcher,
        loadComponent: () =>
          import('./products/product-list/product-list.component').then(
            (m) => m.ProductListComponent
          ),
      },
      {
        path: 'brand/:brand',
        loadComponent: () =>
          import('./products/product-list/product-list.component').then(
            (m) => m.ProductListComponent
          ),
      },
      {
        path: 'product/:page',
        loadComponent: () =>
          import('./products/product-detail/product-detail.component').then(
            (m) => m.ProductDetailComponent
          ),
      },
      {
        path: 'product/oralmedic-vc3a4tskade-tops',
        redirectTo: 'oralmedic-mot-afte-blasor-0-35-ml',
        pathMatch: 'full',
      },
      {
        path: 'chat',
        canActivate: [featureGuard('ragSearch')],
        loadComponent: () => import('./shared/chat/chat.component').then((m) => m.ChatComponent),
      },
      {
        path: 'go',
        loadComponent: () =>
          import('./affiliate/go-continue.component').then((m) => m.GoContinueComponent),
      },
      {
        path: CAMPAIGNS_PATH,
        loadComponent: () =>
          import('./campaigns/campaigns-page.component').then((m) => m.CampaignsPageComponent),
      },
      // Non-SE markets served this page on the Swedish path before it was
      // localized; keep that path alive as a redirect. On SE the two are the
      // same route, so nothing is registered.
      ...(CAMPAIGNS_PATH === LEGACY_CAMPAIGNS_PATH
        ? []
        : [
            {
              path: LEGACY_CAMPAIGNS_PATH,
              redirectTo: `/${CAMPAIGNS_PATH}`,
              pathMatch: 'full' as const,
            },
          ]),
      {
        path: 'article/:article',
        // CMS articles are Swedish, except each EU market's own six legal pages,
        // which are authored per market in the articles table (see LEGAL_ARTICLES).
        canActivate: [legalArticleGuard],
        loadComponent: () =>
          import('./article-cms/article-cms.component').then((m) => m.ArticleCmsComponent),
      },
      {
        path: 'guide/:guide',
        canActivate: [featureGuard('editorialGuides')],
        loadComponent: () =>
          import('./article-cms/article-cms.component').then((m) => m.ArticleCmsComponent),
      },
      {
        path: 'guide',
        canActivate: [featureGuard('editorialGuides')],
        loadComponent: () =>
          import('./article-cms/article-cms.component').then((m) => m.ArticleCmsComponent),
      },
      { path: 'prisguider', redirectTo: '/guide', pathMatch: 'full' },
      {
        path: 'careers',
        loadComponent: () => import('./careers/careers.component').then((m) => m.CareersComponent),
      },
      {
        path: 'alert/:code',
        canActivate: [featureGuard('stockAlerts')],
        loadComponent: () => import('./alert/alert.component').then((m) => m.AlertComponent),
      },
      {
        path: 'cart',
        loadComponent: () => import('./cart/cart.component').then((m) => m.CartComponent),
      },
      {
        path: '',
        loadComponent: () =>
          import('./dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      { path: 'hem', redirectTo: '/', pathMatch: 'full' },
      {
        path: 'scan',
        canActivate: [featureGuard('barcodeScan')],
        loadComponent: () => import('./scan/scan.component').then((m) => m.ScanComponent),
      },
      {
        path: 'widget',
        redirectTo: '/public/widget/index.html',
        pathMatch: 'full',
      },
      {
        path: 'dela/:wishlistId',
        loadComponent: () => import('./cart/cart.component').then((m) => m.CartComponent),
      },
      // Vendor overview, vendor detail and store detail are SE-only for launch.
      // The list query is country-filtered but the detail query (vendor.component.ts
      // loadVendorDetails) resolves any slug regardless of market, so a Swedish
      // vendor renders on an EU host. Guarding the routes is the launch-time fix;
      // country-filtering the detail query is the durable one.
      {
        path: 'butik/:vendorSlug/:storeSlug',
        canActivate: [countryGuard('SE')],
        loadComponent: () =>
          import('./vendor/store-detail/store-detail.component').then(
            (m) => m.StoreDetailComponent
          ),
      },
      {
        path: 'apotek/:vendorSlug/:storeSlug',
        canActivate: [countryGuard('SE')],
        loadComponent: () =>
          import('./vendor/store-detail/store-detail.component').then(
            (m) => m.StoreDetailComponent
          ),
      },
      {
        path: 'butik/:vendorSlug',
        canActivate: [countryGuard('SE')],
        loadComponent: () => import('./vendor/vendor.component').then((m) => m.VendorComponent),
      },
      {
        path: 'apotek/:vendorSlug',
        canActivate: [countryGuard('SE')],
        loadComponent: () => import('./vendor/vendor.component').then((m) => m.VendorComponent),
      },
      // No guard here: redirects resolve before canActivate runs, so this hop is
      // caught by the guard on its target ('apotek').
      {
        path: 'butik',
        redirectTo: 'apotek',
        pathMatch: 'full',
      },
      {
        path: 'apotek',
        canActivate: [countryGuard('SE')],
        loadComponent: () => import('./vendor/vendor.component').then((m) => m.VendorComponent),
      },
      {
        path: 'bast-i-test/:articleId',
        canActivate: [featureGuard('editorialGuides')],
        loadComponent: () =>
          import('./bast-i-test/bast-i-test.component').then((m) => m.BastITestComponent),
      },
      {
        path: 'bast-i-test',
        loadComponent: () =>
          import('./page-not-found/page-not-found.component').then((m) => m.PageNotFoundComponent),
      },
      {
        path: 'restnoteringar',
        canActivate: [featureGuard('drugShortages')],
        loadComponent: () =>
          import('./products/restnoteringar/restnoteringar.component').then(
            (m) => m.RestnoteringarComponent
          ),
      },
      {
        path: 'toplist',
        loadComponent: () =>
          import('./products/toplists/toplist-page.component').then((m) => m.ToplistPageComponent),
      },
      {
        path: 'toplist/:key',
        loadComponent: () =>
          import('./products/toplists/toplist-page.component').then((m) => m.ToplistPageComponent),
      },
      {
        path: '**',
        loadComponent: () =>
          import('./page-not-found/page-not-found.component').then((m) => m.PageNotFoundComponent),
      },
    ],
  },
];
